import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  catchError,
  finalize,
  map,
  shareReplay,
  take,
  tap,
  timeout
} from 'rxjs/operators';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import * as Sentry from '@sentry/angular';
import { jwtDecode } from 'jwt-decode';
import { UserManagementService } from './user-management.service';
import { RedirectService } from './redirect.service';
import { SentryInitService } from './sentry-init.service';
import { LocalDataCleanupService } from './local-data-cleanup.service';

/**
 * Полезная нагрузка access-токена Identix (тенант data1861).
 *
 * <p>Модель приведена к реальному токену Identix. Прежняя редакция описывала токен Keycloak
 * ({@code realm_access}/{@code resource_access}/{@code sid}/{@code session_state}/{@code family_name}/
 * {@code email_verified} и т.п.) — этих полей у Identix НЕТ, поэтому они были либо мёртвыми
 * объявлениями, либо приводили к {@code undefined} при чтении. Роли Identix отдаёт ПЛОСКИМ списком
 * {@code roles} (уровень тенанта), а не вложенным {@code realm_access.roles}.
 *
 * <p>Сегодня приложение читает из токена только {@code email} и {@code preferred_username}
 * (см. {@link AuthService#loadUserProfile}, {@code BaseCacheService}); разграничения UI по ролям нет.
 * Остальные поля описаны для точности контракта и на будущее.
 */
export interface JWTToken {
  iss: string;
  sub: string; // идентификатор пользователя в Identix (identix_id); прежде здесь ошибочно ждали sid
  aud: string[];
  exp: number;
  iat: number;
  nbf: number;
  jti: string;
  scope: string[];
  tenant_id: number;
  epoch: number;
  preferred_username: string;
  email: string;
  name: string; // единое отображаемое имя (у Identix нет раздельных given_name/family_name)
  roles: string[]; // плоский список ролей тенанта, напр. ['USER'] или ['MANAGER','USER']
  groups: string[];
  organizations: unknown[]; // ИНН организаций пользователя
  metadata: Record<string, unknown>;
  password_change_required: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  /**
   * Роль доступа в приложение экспедирования. В Identix маппится на группу forwarders (маппинг
   * группа→роль), поэтому у каждого форвардера она есть в токене. Единый источник для гейта входа
   * ({@code auth.component}) и маршрутного guard ({@code AppAuthGuard}).
   */
  public static readonly FORWARDER_ROLE = 'FORWARDER';

  /**
   * Предел ожидания каждой задачи выхода (аннулирование серверной сессии, очистка локальных данных)
   * перед перезагрузкой: не ждём вечно при висящей сети или заблокированной БД.
   */
  private static readonly LOGOUT_TASK_TIMEOUT_MS = 3000;
  // Предел ожидания обновления токена. Без него зависший запрос (принятое соединение без ответа —
  // обычное дело на мобильной сети) навсегда заклинил бы refreshInProgress$, а вместе с ним и все
  // последующие 401. По таймауту рейс завершается ошибкой → finalize освобождает его → ожидающие
  // получают ошибку и штатно выходят из сессии (handle401Error), а не виснут до перезапуска.
  private static readonly REFRESH_TIMEOUT_MS = 30000;

  /**
   * Текущее обновление токена как единый «одиночный рейс». Пока оно не завершилось, все параллельные
   * 401 подписываются на него же, а не запускают конкурирующие обновления (ротация refresh-токена
   * завалила бы все, кроме первого). {@code null} — обновления сейчас нет.
   */
  private refreshInProgress$: Observable<string> | null = null;

  /** Защита от повторного выхода из сессии: параллельные провалы обновления не должны давать N перезагрузок. */
  private isLoggingOut = false;

  constructor(
    private userManagementService: UserManagementService,
    private redirectService: RedirectService,
    private sentryInitService: SentryInitService,
    private router: Router,
    private localDataCleanup: LocalDataCleanupService
  ) {}

  /**
   * Единый предикат наличия роли экспедитора. Проверяем, что claim {@code roles} — именно массив:
   * при строковом значении {@code String.prototype.includes} сработал бы по подстроке и пропустил бы
   * посторонний токен.
   */
  private static rolesHaveForwarder(roles: unknown): boolean {
    return Array.isArray(roles) && roles.includes(AuthService.FORWARDER_ROLE);
  }

  public isLoggedIn(): boolean {
    return !!this.getAccessToken();
  }

  public login(
    token: string,
    refreshToken?: string,
    ttl?: number
  ): Promise<boolean> {
    // Ограничение входа по роли — то же, что и при входе «от имени» (impersonateLogin): в приложение
    // экспедирования допускаем только пользователей с ролью FORWARDER. Основную проверку делает экран
    // входа до вызова этого метода; проверка здесь дублирует её на уровне сервиса, чтобы правило «нет
    // сессии без роли экспедитора» держалось независимо от вызывающего. При отказе токен не сохраняется.
    if (!this.tokenHasForwarderRole(token)) {
      return Promise.resolve(false);
    }
    this.saveAccessToken(token);
    // Смену субъекта проверяем ПОСЛЕ сохранения токена (новый e-mail читается из него), но ДО
    // setEmailToLocalStore (которое перезапишет прежний 'login').
    this.cleanupPreviousSubjectIfChanged();
    // 'login' обновляем БЕЗУСЛОВНО: он служит указателем текущего субъекта для очистки данных при
    // следующей смене. Прежде это делалось только при наличии ttl — вход без ttl оставил бы указатель
    // устаревшим, и данные промежуточного субъекта не были бы очищены.
    this.setEmailToLocalStore();
    // refresh-токен сохраняем при его наличии, не завися от ttl: Identix может вернуть
    // refresh_expires_in=0 при валидном самом токене, а ttl в приложении не используется (обновление
    // реактивное — по 401 в перехватчике). Прежнее условие «&& ttl» отбрасывало исправный refresh-токен
    // при нулевом сроке, и сессия не продлевалась (пользователя выкидывало после истечения access-токена).
    if (refreshToken) {
      this.saveRefreshToken(refreshToken, ttl);
    }

    return this.redirectService.checkRedirectPath();
  }

  /**
   * Вход «от имени» (имперсонация). Тот же гейт по роли, что и обычный вход: в приложение
   * экспедирования пускаем только пользователей с ролью FORWARDER. Роль проверяем в переданном токене
   * ДО его сохранения — при отказе токен не сохраняется, сессии не возникает. Возвращает признак
   * успеха, чтобы вызывающий увёл на нужный экран.
   */
  public impersonateLogin(
    token: string,
    refreshToken?: string,
    ttl?: number
  ): boolean {
    if (!this.tokenHasForwarderRole(token)) {
      return false;
    }

    this.saveAccessToken(token);
    // Смену субъекта проверяем ПОСЛЕ сохранения токена (новый e-mail читается из него), но ДО
    // setEmailToLocalStore (которое перезапишет прежний 'login').
    this.cleanupPreviousSubjectIfChanged();
    // 'login' обновляем БЕЗУСЛОВНО: он служит указателем текущего субъекта для очистки данных при
    // следующей смене. Прежде это делалось только при наличии ttl — вход без ttl оставил бы указатель
    // устаревшим, и данные промежуточного субъекта не были бы очищены.
    this.setEmailToLocalStore();
    // refresh-токен сохраняем при его наличии, не завися от ttl: Identix может вернуть
    // refresh_expires_in=0 при валидном самом токене, а ttl в приложении не используется (обновление
    // реактивное — по 401 в перехватчике). Прежнее условие «&& ttl» отбрасывало исправный refresh-токен
    // при нулевом сроке, и сессия не продлевалась (пользователя выкидывало после истечения access-токена).
    if (refreshToken) {
      this.saveRefreshToken(refreshToken, ttl);
    }

    return true;
  }

  /**
   * Завершение регистрации. Гейт по роли здесь НЕ дублируется намеренно, в отличие от {@link #login}
   * и {@link #impersonateLogin}: регистрация в приложении экспедирования всегда создаёт форвардера —
   * бэкенд синхронно заводит пользователя в группе forwarders, из которой роль FORWARDER попадает в
   * токен сразу. Поэтому маршрутный {@link AppAuthGuard} пропустит только что зарегистрированного
   * пользователя, а не выбросит его.
   */
  public registrationEnd(
    token: string,
    refreshToken?: string,
    ttl?: number
  ): Promise<boolean> {
    this.saveAccessToken(token);
    // Смену субъекта проверяем ПОСЛЕ сохранения токена (новый e-mail читается из него), но ДО
    // setEmailToLocalStore (которое перезапишет прежний 'login').
    this.cleanupPreviousSubjectIfChanged();
    // 'login' обновляем БЕЗУСЛОВНО: он служит указателем текущего субъекта для очистки данных при
    // следующей смене. Прежде это делалось только при наличии ttl — вход без ttl оставил бы указатель
    // устаревшим, и данные промежуточного субъекта не были бы очищены.
    this.setEmailToLocalStore();
    // refresh-токен сохраняем при его наличии, не завися от ttl: Identix может вернуть
    // refresh_expires_in=0 при валидном самом токене, а ttl в приложении не используется (обновление
    // реактивное — по 401 в перехватчике). Прежнее условие «&& ttl» отбрасывало исправный refresh-токен
    // при нулевом сроке, и сессия не продлевалась (пользователя выкидывало после истечения access-токена).
    if (refreshToken) {
      this.saveRefreshToken(refreshToken, ttl);
    }

    // После регистрации идём тем же пост-авторизационным путём, что и обычный вход
    // (банковские реквизиты, иначе — заявки). Прежде здесь был экран интеграции SmartSeeds,
    // снятый вместе с отказом от SmartSeeds.
    return this.redirectService.checkRedirectPath();
  }

  public getAccessToken(): string {
    return localStorage.getItem('token') || '';
  }

  public logout() {
    // Идемпотентность: при параллельных провалах обновления сюда прилетает несколько вызовов —
    // выполняем выход один раз (иначе несколько killAuthSession и несколько перезагрузок страницы).
    if (this.isLoggingOut) {
      return;
    }
    this.isLoggingOut = true;

    // e-mail текущего субъекта берём ДО очистки localStorage — по нему чистим его локальные данные.
    const email = this.getDecodedAccessToken()?.email ?? this.getLoginEmail();
    const refreshToken = this.getRefreshToken();

    // Оба действия — аннулирование серверной сессии и очистка локальных данных — выполняем ДО reload:
    // reload прерывает незавершённые операции, поэтому раньше killAuthSession не успевал уйти, а
    // очистка IndexedDB не завершалась бы. timeout на каждой задаче — не ждём вечно при висящей сети
    // или заблокированной БД; в любом исходе (успех/ошибка/таймаут) выполняем локальный выход в
    // finalize ровно один раз.
    const tasks: Observable<unknown>[] = [
      this.localDataCleanup
        .clearSubjectData(email)
        .pipe(timeout(AuthService.LOGOUT_TASK_TIMEOUT_MS), catchError(() => of(undefined)))
    ];
    if (refreshToken) {
      tasks.push(
        this.userManagementService.killAuthSession(refreshToken).pipe(
          take(1),
          timeout(AuthService.LOGOUT_TASK_TIMEOUT_MS),
          catchError(() => of(undefined))
        )
      );
    }

    forkJoin(tasks)
      .pipe(finalize(() => this.logoutActions()))
      .subscribe();
  }

  /**
   * Обновляет access-токен. Единый «одиночный рейс»: параллельные 401 получают ОДНО общее обновление.
   *
   * <p>Успех — все ожидающие получают новый access-токен. ОШИБКА (в т.ч. сетевой сбой обновления)
   * приходит всем ожидающим через канал ошибок {@code shareReplay}, поэтому никто не зависает —
   * прежняя редакция при провале эмитила пустую строку, и ждущие на {@code filter(token !== '')}
   * висели вечно. Значение эмиссии (access-токен) единственный потребитель ({@code auth.interceptor})
   * игнорирует — он перечитывает токен из localStorage; тип оставлен строковым для ясности контракта.
   */
  public refreshToken(): Observable<string> {
    if (this.refreshInProgress$) {
      return this.refreshInProgress$;
    }

    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      // Обновлять нечем — выходим из сессии и пробрасываем ошибку (не оставляем ожидающих висеть).
      this.logout();
      return throwError(
        () => new Error('Попытка обновления токена с пустым refresh токеном')
      );
    }

    this.refreshInProgress$ = this.userManagementService
      .refreshToken(refreshToken)
      .pipe(
        tap(response => {
          // Токен сохраняем ПЕРВЫМ: setEmailToLocalStore читает access-токен из localStorage —
          // до сохранения он взял бы СТАРЫЙ токен (важно при смене субъекта/имперсонации).
          this.saveAccessToken(response.data.access_token);
          this.saveRefreshToken(
            response.data.refresh_token,
            response.data.refresh_expires_in
          );
          this.setEmailToLocalStore();
        }),
        map(response => response.data.access_token),
        timeout(AuthService.REFRESH_TIMEOUT_MS),
        finalize(() => {
          // Освобождаем рейс: следующий 401 после завершения (успех, ошибка или таймаут) начнёт новое обновление.
          this.refreshInProgress$ = null;
        }),
        // Общий поток на всех ожидающих. bufferSize 1 — последнее значение/ошибка отдаётся и тем,
        // кто подписался чуть позже, в пределах активного рейса.
        shareReplay({ bufferSize: 1, refCount: false })
      );

    return this.refreshInProgress$;
  }

  public getRefreshToken() {
    return localStorage.getItem('refresh-token') || '';
  }

  /**
   * Профиль пользователя из access-токена Identix.
   *
   * <p>Читаем только то, что Identix реально присылает: {@code sub} (идентификатор), {@code email},
   * {@code preferred_username}, единое отображаемое имя {@code name}. Прежние поля {@code lastName}
   * (из {@code family_name}) и {@code emailVerified} (из {@code email_verified}) убраны — у Identix
   * таких claim нет, они всегда были {@code undefined} и никем не читались (имя на экране аккаунта
   * приходит с бэкенда, а не из токена). При отсутствии/повреждении токена {@link #parseJwt}
   * вернёт {@code null}, и все поля станут {@code undefined} — потребители это учитывают.
   */
  public loadUserProfile() {
    const token = this.getDecodedAccessToken();
    return {
      id: token?.sub,
      username: token?.preferred_username,
      email: token?.email,
      name: token?.name
    };
  }

  /**
   * Декодированный текущий access-токен (или {@code null}). Единая точка разбора JWT в приложении:
   * тем же путём пользуется {@code BaseCacheService} — чтобы не держать второй декодер.
   */
  public getDecodedAccessToken(): JWTToken | null {
    return this.parseJwt(this.getAccessToken());
  }

  /**
   * Декодирует ПЕРЕДАННЫЙ access-токен (ещё не сохранённый в localStorage). Нужен гейту входа, который
   * проверяет роль в токене до его сохранения; {@link #getDecodedAccessToken} для этого не годится —
   * он читает уже сохранённый токен.
   */
  public decodeToken(token: string): JWTToken | null {
    return this.parseJwt(token);
  }

  /**
   * Есть ли в СОХРАНЁННОМ токене роль экспедитора. Для маршрутного guard: закрывает доступ по старому
   * токену (вошёл до внедрения гейта) и после обновления токена, когда экранный гейт входа минуется.
   */
  public hasForwarderRole(): boolean {
    return AuthService.rolesHaveForwarder(this.getDecodedAccessToken()?.roles);
  }

  /**
   * Есть ли роль экспедитора в ПЕРЕДАННОМ (ещё не сохранённом) токене. Единая точка для экранного
   * гейта входа и имперсонации, проверяющих роль до сохранения токена.
   */
  public tokenHasForwarderRole(token: string): boolean {
    return AuthService.rolesHaveForwarder(this.decodeToken(token)?.roles);
  }

  public setEmailToLocalStore() {
    const { email } = this.loadUserProfile();
    if (email) {
      this.sentryInitService.setUserContextByEmail(email);
      localStorage.setItem('login', email);
    }
  }

  /** e-mail сохранённого субъекта из localStorage (пусто/{@code null} → {@code null}). */
  private getLoginEmail(): string | null {
    return localStorage.getItem('login') || null;
  }

  /**
   * Если в приложение вошёл ДРУГОЙ субъект, очищает локальные данные предыдущего (кэши списков
   * заказов). Очереди несинхронизированных действий не затрагиваются — см.
   * {@link LocalDataCleanupService}. «Выстрелил и забыл»: вход не ждёт и не зависит от очистки.
   */
  private cleanupPreviousSubjectIfChanged(): void {
    const previousEmail = this.getLoginEmail();
    const currentEmail = this.getDecodedAccessToken()?.email ?? null;
    if (previousEmail && currentEmail && previousEmail !== currentEmail) {
      this.localDataCleanup.clearSubjectData(previousEmail).subscribe();
    }
  }

  private logoutActions() {
    localStorage.setItem('login', '');
    localStorage.setItem('token', '');
    localStorage.setItem('refresh-token', '');
    localStorage.setItem('ttl', '');
    this.sentryInitService.setUserContextByEmail(null);
    this.router.navigate(['auth']);
    // Сбрасываем флаг ПЕРЕД перезагрузкой: обычно reload выгружает страницу и флаг неважен, но если
    // reload будет подавлен (webview/тест/перехват навигации), следующий выход не окажется заблокирован.
    this.isLoggingOut = false;
    window.location.reload();
  }

  /**
   * Разбор payload JWT через библиотеку {@code jwt-decode} — единственный декодер приложения
   * (заменил ручной {@code atob}+UTF-8 и продублированный декодер в {@code BaseCacheService};
   * корректность кириллицы в {@code name} проверена на живом токене Identix).
   *
   * <p>Префикс «Bearer » снимаем ЯВНО, а не полагаемся на то, что он попадёт в отбрасываемый
   * header-сегмент. Сам токен в Sentry НЕ передаём: это учётные данные, их нельзя писать в трекер.
   */
  private parseJwt(token: string | null): JWTToken | null {
    if (!token) {
      return null;
    }
    try {
      return jwtDecode<JWTToken>(token.replace(/^Bearer\s+/i, ''));
    } catch (e) {
      Sentry.captureException(e);
      return null;
    }
  }

  private saveAccessToken(token: string): void {
    localStorage.setItem('token', `Bearer ${token}`);
  }

  private saveRefreshToken(token: string, ttl?: number): void {
    localStorage.setItem('refresh-token', `${token}`);
    localStorage.setItem('ttl', `${ttl}`);
  }
}
